import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { IPersonnelOnBoard } from 'src/app/crew-management-user/models/current-personnel/personnel-on-board.model';
import { IAdditionalTimeResponse } from '../models/additional-time.model';
import {
  IAdditionalTimeDraftBreachWarning,
  IDailyCrewWorkOverviewResult,
  IDailyCrewWorkOverviewRow,
} from '../models/daily-crew-work-overview.model';
import { IWorkOverviewMatrixRow } from '../models/work-overview-matrix.model';
import {
  ClearDailyCrewWorkOverview,
  LoadWorkOverviewDetailsById,
  LoadWorkOverviewList,
  PrintWorkOverviewById,
  PrintWorkOverviews,
  LoadAlternativeWorkOverviewById,
  LoadAvailablePersonnel,
  ClearCurrentWorkOverview,
  LoadDailyCrewWorkOverview,
} from './work-overview.actions';
import { WorkOverviewApiService } from '../services/work-overview-api.service';
import {
  IAlternativeWorkOverviewResponse,
  IWorkOverviewDetails,
} from '../models/work-overview-details.model';

export interface WorkOverviewStateModel {
  workOverviews: IWorkOverviewMatrixRow[];
  workOverviewsTotalPages: number;
  currentWorkOverview: IWorkOverviewDetails;
  currentWorkOverviewMatrixPdf: Blob | undefined;
  currentWorkOverviewByIdPdf: Blob | undefined;
  alternativeWorkOverview: IAlternativeWorkOverviewResponse;
  availablePersonnel: IPersonnelOnBoard[];
  dailyCrewWorkOverview: IDailyCrewWorkOverviewResult;
}

const WORK_OVERVIEW_STATE_TOKEN = new StateToken<WorkOverviewStateModel>('workOverviews');

@State<WorkOverviewStateModel>({
  name: WORK_OVERVIEW_STATE_TOKEN,
  defaults: {
    workOverviews: [],
    workOverviewsTotalPages: 0,
    currentWorkOverview: <IWorkOverviewDetails>{},
    currentWorkOverviewMatrixPdf: undefined,
    currentWorkOverviewByIdPdf: undefined,
    alternativeWorkOverview: <IAlternativeWorkOverviewResponse>{},
    availablePersonnel: [],
    dailyCrewWorkOverview: {
      rows: [],
      previewAdditionalTimes: [],
      additionalTimeDraftBreachWarnings: [],
    },
  },
})
@Injectable()
export class WorkOverviewState {
  constructor(private service: WorkOverviewApiService) {}

  @Action(LoadWorkOverviewList, { cancelUncompleted: true })
  loadWorkOverviewList(
    { getState, setState }: StateContext<WorkOverviewStateModel>,
    { date, filter }: LoadWorkOverviewList
  ) {
    return this.service.getWorkOverviews(date, filter).pipe(
      tap((response: ApiResponse<IWorkOverviewMatrixRow[]>) => {
        const state = getState();
        setState({
          ...state,
          workOverviews: response.data,
          workOverviewsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadWorkOverviewDetailsById, { cancelUncompleted: true })
  loadWorkOverviewDetailsById(
    { getState, setState }: StateContext<WorkOverviewStateModel>,
    { id, year, month, showFuture, showWorkPlan }: LoadWorkOverviewDetailsById
  ) {
    return this.service.getWorkOverviewById(id, year, month, showFuture, showWorkPlan).pipe(
      tap((response: IWorkOverviewDetails) => {
        const state = getState();
        setState({
          ...state,
          currentWorkOverview: response,
        });
      })
    );
  }

  @Action(LoadAlternativeWorkOverviewById, { cancelUncompleted: true })
  loadAlternativeWorkOverviewById(
    { getState, setState }: StateContext<WorkOverviewStateModel>,
    { id, year, month }: LoadAlternativeWorkOverviewById
  ) {
    return this.service.getAlternativeWorkOverviewById(id, year, month).pipe(
      tap((response: IAlternativeWorkOverviewResponse) => {
        const state = getState();
        setState({
          ...state,
          alternativeWorkOverview: response,
        });
      })
    );
  }

  @Action(PrintWorkOverviewById, { cancelUncompleted: true })
  printWorkOverviewById(
    { getState, setState }: StateContext<WorkOverviewStateModel>,
    { id, year, month }: PrintWorkOverviewById
  ) {
    return this.service.printWorkOverviewById(id, year, month).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          currentWorkOverviewByIdPdf: file,
        });
      })
    );
  }

  @Action(PrintWorkOverviews, { cancelUncompleted: true })
  printWorkOverviews(
    { getState, setState }: StateContext<WorkOverviewStateModel>,
    { year, month }: PrintWorkOverviews
  ) {
    return this.service.printWorkOverviews(year, month).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          currentWorkOverviewMatrixPdf: file,
        });
      })
    );
  }

  @Action(LoadAvailablePersonnel)
  loadAvailablePersonnel(
    { getState, setState }: StateContext<WorkOverviewStateModel>,
    {
      hasAssignedWorkSchedule,
      isForWorkPlan,
      from,
      to,
      isForWorkScheduleAssignment,
    }: LoadAvailablePersonnel
  ) {
    return this.service
      .getAvailablePersonnel(
        hasAssignedWorkSchedule,
        isForWorkPlan,
        from,
        to,
        isForWorkScheduleAssignment
      )
      .pipe(
        tap((personnel: IPersonnelOnBoard[]) => {
          setState({
            ...getState(),
            availablePersonnel: personnel,
          });
        })
      );
  }

  @Action(LoadDailyCrewWorkOverview, { cancelUncompleted: true })
  loadDailyCrewWorkOverview(
    { getState, setState }: StateContext<WorkOverviewStateModel>,
    { request }: LoadDailyCrewWorkOverview
  ) {
    return this.service.getDailyCrewWorkOverview(request).pipe(
      tap((response: IDailyCrewWorkOverviewResult) => {
        const normalizedRows =
          response.rows?.map((row) => ({
            ...row,
            assignedPosition: row.assignedPosition ?? 'N/A',
            unitCompliances: row.unitCompliances ?? [],
          })) ?? [];

        setState({
          ...getState(),
          dailyCrewWorkOverview: {
            ...response,
            rows: normalizedRows,
            previewAdditionalTimes: response.previewAdditionalTimes ?? [],
            additionalTimeDraftBreachWarnings: response.additionalTimeDraftBreachWarnings ?? [],
          },
        });
      })
    );
  }

  @Action(ClearCurrentWorkOverview)
  clearCurrentWorkOverview(ctx: StateContext<WorkOverviewStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentWorkOverview: <IWorkOverviewDetails>{},
    });
  }

  @Action(ClearDailyCrewWorkOverview)
  clearDailyCrewWorkOverview(ctx: StateContext<WorkOverviewStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      dailyCrewWorkOverview: {
        rows: [],
        previewAdditionalTimes: [],
        additionalTimeDraftBreachWarnings: [],
      },
    });
  }

  @Selector()
  static getWorkOverviewsTotalPages(state: WorkOverviewStateModel) {
    return state.workOverviewsTotalPages;
  }

  @Selector()
  static getWorkOverviews(state: WorkOverviewStateModel): IWorkOverviewMatrixRow[] {
    return state.workOverviews;
  }

  @Selector()
  static getWorkOverviewById(state: WorkOverviewStateModel) {
    return state.currentWorkOverview;
  }

  @Selector()
  static getAlternativeWorkOverviewById(state: WorkOverviewStateModel) {
    return state.alternativeWorkOverview;
  }

  @Selector()
  static getWorkOverviewByIdPdf(state: WorkOverviewStateModel) {
    return state.currentWorkOverviewByIdPdf;
  }

  @Selector()
  static getWorkOverviewMatrixPdf(state: WorkOverviewStateModel) {
    return state.currentWorkOverviewMatrixPdf;
  }

  @Selector()
  static getAvailablePersonnel(state: WorkOverviewStateModel) {
    return state.availablePersonnel;
  }

  @Selector()
  static getDailyCrewWorkOverview(state: WorkOverviewStateModel) {
    return state.dailyCrewWorkOverview;
  }

  @Selector()
  static getDailyCrewWorkOverviewRows(state: WorkOverviewStateModel): IDailyCrewWorkOverviewRow[] {
    return state.dailyCrewWorkOverview.rows ?? [];
  }

  @Selector()
  static getDailyCrewWorkOverviewPreviewAdditionalTimes(
    state: WorkOverviewStateModel
  ): IAdditionalTimeResponse[] {
    return state.dailyCrewWorkOverview.previewAdditionalTimes ?? [];
  }

  @Selector()
  static getDailyCrewWorkOverviewAdditionalTimeDraftBreachWarnings(
    state: WorkOverviewStateModel
  ): IAdditionalTimeDraftBreachWarning[] {
    return state.dailyCrewWorkOverview.additionalTimeDraftBreachWarnings ?? [];
  }
}
